import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Accordion from '../Accordion/Accordion';
import AccordionItem from '../Accordion/AccordionItem';
import Address from '../Address/Address';
import { useHistory } from 'react-router-dom';
import { constantToLabel } from '@ecosconnect/core/src/helpers/string';
import useMode from '../../hooks/useMode';
import { Text, Flex, Box } from 'rebass';
import DataTable from '../Table/DataTable';
import waterServiceColumns from './WaterSiteServices.columns';
import { EmailInput, PhoneInput, ProfileType } from '../Profile/types';
import { format, parseISO } from 'date-fns';
import { WaterSiteType } from './WaterSiteTypePicker';
import { SourceType } from '../WaterService/SourceTypePicker';
import StatusChip from '../Status/StatusChip';
import StatusDot from '../Status/StatusDot';

export type WaterSite = {
  id: string;
  name: string;
  siteNumber?: string;
  type: WaterSiteType;
  lastSurveyDate?: string;
  nextSurveyDate?: string;
  surveyIntervalYears: number;
  lastInspectionDate?: string;
  nextInspectionDate?: string;
  recycledWaterServices: {
    id: string;
    sourceType: SourceType;
    siteNumber: string;
    meterNumber: string;
    pin: string;
    type: string;
    status: 'ACTIVE' | 'INACTIVE';
  }[];
  hazardWaterServices: {
    id: string;
    sourceType: SourceType;
    siteNumber: string;
    meterNumber: string;
    pin: string;
    type: string;
    status: 'ACTIVE' | 'INACTIVE';
    waterServiceHazards?: {
      waterHazardId: string;
      isProtected: 'YES' | 'NO' | 'UNKNOWN' | null;
      waterHazard: {
        id: string;
        name: string;
        hazardLevel: 'HIGH' | 'LOW';
      };
    }[];
  }[];
  contactProfileId?: string;
  contact?: {
    id: string;
    type: ProfileType;
    name: string;
    firstName: string;
    lastName: string;
    phones: PhoneInput[];
    emails: EmailInput[];
  };
  ownerProfileId?: string;
  owner?: {
    id: string;
    type: ProfileType;
    name: string;
    firstName: string;
    lastName: string;
    phones: PhoneInput[];
    emails: EmailInput[];
  };
  supervisor?: {
    id: string;
    type: ProfileType;
    name: string;
    firstName: string;
    lastName: string;
    phones: PhoneInput[];
    emails: EmailInput[];
  };
  assignedTo?: {
    id: string;
    name: string;
    firstName: string;
    lastName: string;
    phones: PhoneInput[];
    emails: EmailInput[];
  };
  address: {
    id: string;
    street1: string;
    street2: string;
    city: string;
    state: string;
    zip: string;
    zipExt: string;
  };
  tags?: string[];
};

type WaterSiteDetailProps = {
  waterSite: WaterSite;
  onChange?: () => void;
};

const WaterSiteDetail: React.FC<WaterSiteDetailProps> = ({
  waterSite,
  onChange,
}) => {
  const history = useHistory();
  const mode = useMode();

  const waterServices =
    waterSite.type === 'HAZARD'
      ? waterSite.hazardWaterServices
      : waterSite.recycledWaterServices;

  const waterServiceIds = useMemo(
    () => waterServices.map((service) => service.id),
    [waterServices]
  );

  const ownerAndContactAreSame =
    waterSite.contactProfileId &&
    waterSite.ownerProfileId &&
    waterSite.contactProfileId === waterSite.ownerProfileId;

  return (
    <>
      <Accordion title="Details">
        <AccordionItem label="Type" value={constantToLabel(waterSite.type)} />
        {waterSite.siteNumber && (
          <AccordionItem label="Site Number" value={waterSite.siteNumber} />
        )}
        <AccordionItem
          label="Address"
          value={<Address address={waterSite.address} />}
        />
        {waterSite.assignedTo && (
          <AccordionItem
            label="Assigned To"
            value={`${waterSite.assignedTo.firstName} ${waterSite.assignedTo.lastName}`}
          />
        )}

        {waterSite.lastSurveyDate && (
          <AccordionItem
            label="Last Surveyed"
            value={format(parseISO(waterSite.lastSurveyDate), 'M/d/yyyy')}
          />
        )}
        {waterSite.lastInspectionDate && (
          <AccordionItem
            label="Last Inspected"
            value={format(parseISO(waterSite.lastInspectionDate), 'M/d/yyyy')}
          />
        )}
      </Accordion>

      {waterSite.owner && (
        <Accordion title={ownerAndContactAreSame ? 'Owner/Contact' : 'Owner'}>
          <AccordionItem label="Name" value={waterSite.owner.name} />
          {waterSite.owner.phones.length > 0 && (
            <AccordionItem
              label="Phone"
              value={waterSite.owner.phones[0].value}
            />
          )}
          {waterSite.owner.emails.length > 0 && (
            <AccordionItem
              label="Email"
              value={waterSite.owner.emails[0].value}
            />
          )}
        </Accordion>
      )}

      {waterSite.contact && !ownerAndContactAreSame && (
        <Accordion title="Contact">
          <AccordionItem label="Name" value={waterSite.contact.name} />
          {waterSite.contact.phones.length > 0 && (
            <AccordionItem
              label="Phone"
              value={waterSite.contact.phones[0].value}
            />
          )}
          {waterSite.contact.emails.length > 0 && (
            <AccordionItem
              label="Email"
              value={waterSite.contact.emails[0].value}
            />
          )}
        </Accordion>
      )}

      {waterSite.supervisor && (
        <Accordion title="Supervisor">
          <AccordionItem
            label="Name"
            value={`${waterSite.supervisor.firstName} ${waterSite.supervisor.lastName}`}
          />
          {waterSite.supervisor.phones.length > 0 && (
            <AccordionItem
              label="Phone"
              value={waterSite.supervisor.phones[0].value}
            />
          )}
          {waterSite.supervisor.emails.length > 0 && (
            <AccordionItem
              label="Email"
              value={waterSite.supervisor.emails[0].value}
            />
          )}
        </Accordion>
      )}

      <Accordion title="Water Services">
        {waterSite.type === 'HAZARD' && waterServices && waterServices.length ? (
          <Flex flexDirection="column" sx={{ gap: 0 }}>
            {(waterServices as WaterSite['hazardWaterServices']).map((svc) => (
              <Flex
                key={svc.id}
                alignItems="flex-start"
                justifyContent="space-between"
                px={3}
                py={2}
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'fieldBorder',
                  cursor: 'pointer',
                  '&:hover': { bg: 'backgroundHover' },
                }}
                onClick={() =>
                  history.push(`/${mode}/water-services/${svc.id}`)
                }
              >
                <Flex flexDirection="column">
                  <Text variant="body2" fontWeight="bold">
                    {svc.siteNumber}
                  </Text>
                  {svc.meterNumber && (
                    <Text variant="body2" color="gray.500" fontSize={0}>
                      {svc.meterNumber}
                    </Text>
                  )}
                </Flex>
                {svc.waterServiceHazards && svc.waterServiceHazards.length > 0 && (
                  <Flex
                    flexWrap="wrap"
                    sx={{ gap: 1 }}
                    justifyContent="flex-end"
                    maxWidth="60%"
                  >
                    {svc.waterServiceHazards.map((h) => (
                      <StatusChip
                        key={h.waterHazardId}
                        status={
                          h.isProtected === 'YES'
                            ? 'success'
                            : h.isProtected === 'NO'
                              ? 'error'
                              : 'warning'
                        }
                        fontSize={0}
                      >
                        <Flex alignItems="center" sx={{ gap: '4px' }}>
                          <StatusDot
                            status={
                              h.waterHazard.hazardLevel === 'HIGH'
                                ? 'error'
                                : 'warning'
                            }
                            sx={{ flexShrink: 0 }}
                          />
                          {h.waterHazard.name}
                        </Flex>
                      </StatusChip>
                    ))}
                  </Flex>
                )}
              </Flex>
            ))}
          </Flex>
        ) : waterServices && waterServices.length ? (
          <DataTable
            columns={waterServiceColumns}
            itemIds={waterServiceIds}
            onRowClick={(item: (typeof waterServices)[0]) => {
              history.push(`/${mode}/water-services/${item.id}`);
            }}
            items={waterServices}
          />
        ) : (
          <Text variant="body2" color="fieldBorderHover">
            There are no water services assigned to this site.
          </Text>
        )}
      </Accordion>
    </>
  );
};

WaterSiteDetail.propTypes = {
  waterSite: PropTypes.any,
};

export default WaterSiteDetail;
